/* eslint-disable no-console */
import { usePushNotificationContext } from '@context/PushNotificationContext';
import { clearCache } from '@services/cache';
import { resetStore } from '@store/index';
import { EMPTY_VALUE, TELEMETRY_STORAGE_KEY } from '@utils/constants';
import { resetUserHash } from '@utils/analytics/sanitize';
import { identify } from '@utils/analytics/telemetry';
import useStoredState from '@utils/hooks/useStoredState';
import { usePostHog } from 'posthog-js/react';
import { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import type { UserType } from '@interfaces/general';
import type { DeliveryPersonSessionData } from '@interfaces/DeliveryPerson';
import { clearDeliveryPersonSession } from '@services/deliveryPersonAxios';
import { resetFeatureVersionStore } from '@utils/featureRollout/store';
import { resetOutboxDraining } from '@utils/featureRollout/outbox';
import { resetPreviewState } from '@utils/featureRollout/preview';
import { resetFeatureVersionHydration } from '@hooks/useFeatureVersion';

interface UserModule {
  id: string;
  permisos: string[];
}

// Multisucursal: identidad de la sucursal a la que se cambia, para mantener consistencia
// en localStorage (nombre/logo/path) y re-identificar telemetría tras el switch.
interface BranchIdentity {
  nombre?: string;
  logotipo?: string;
  id_path?: string;
}

interface loginProps {
  id_commerce: string;
  id_user: string;
  id_path: string;
  commerce_name: string;
  commerce_logo: string;
  user_name: string;
  token: string;
  user: {
    email: string;
    username: string;
    name: string;
    status: string;
  };
  onboarding_finish_date: string | null;
  // PLI-2031: user_type del JWT
  user_type?: UserType;
  // Multisucursal: persistir is_root y módulos del usuario. El backend lo envía como
  // string 'si'/'no' (no boolean) — se normaliza a booleano en login().
  is_root?: boolean | string;
  user_modules?: UserModule[];
}

interface AuthContextProps {
  isAuthenticated: boolean;
  isReady: boolean;
  commerceName: string;
  onboardingDate: loginProps['onboarding_finish_date'];
  setCommerceName: (param: string) => void;
  commerceLogo: string;
  setCommerceLogo: (param: string) => void;
  commercePath: string;
  setCommercePath: (param: string) => void;
  userId: string;
  userName: string;
  login: (params: loginProps) => void;
  logout: () => void;
  refreshLogin: (userToken: string, commerceId: string, identity?: BranchIdentity) => void;
  checkAuth: () => void;
  user: {
    email: string;
    name: string;
    username: string;
    status: string;
  };
  // PLI-2031: Acceso delegado a repartidores
  userType: UserType | null;
  isDeliveryPerson: boolean;
  deliveryPersonJwt: string | null;
  deliveryOrderId: string | null;
  deliveryDriverName: string | null;
  loginAsDeliveryPerson: (data: DeliveryPersonSessionData) => void;
  // Multisucursal: is_root y módulos del usuario
  isRoot: boolean;
  hasUserModule: (moduleKey: string) => boolean;
}

const AuthContext = createContext<AuthContextProps>({
  isAuthenticated: false,
  isReady: true,
  commerceName: '',
  onboardingDate: '',
  setCommerceName: () => {
    window.console.log('Empty method setCommerceName');
  },
  commerceLogo: '',
  setCommerceLogo: () => {
    window.console.log('Empty method setCommerceLogo');
  },
  commercePath: '',
  setCommercePath: () => {
    window.console.log('Empty method setCommercePath');
  },
  userId: '',
  userName: '',
  refreshLogin: () => {
    window.console.log('Empty method refreshLogin');
  },
  login: () => {
    window.console.log('Empty method login');
  },
  logout: () => {
    window.console.log('Empty method logout');
  },
  checkAuth: () => {
    window.console.log('Empty method checkAuth');
  },
  user: {
    email: '',
    username: '',
    name: '',
    status: '',
  },
  // PLI-2031
  userType: null,
  isDeliveryPerson: false,
  deliveryPersonJwt: null,
  deliveryOrderId: null,
  deliveryDriverName: null,
  loginAsDeliveryPerson: () => {
    window.console.log('Empty method loginAsDeliveryPerson');
  },
  // Multisucursal
  isRoot: false,
  hasUserModule: () => false,
});

const useAuth = () => {
  return useContext(AuthContext);
};

const AuthComponent = ({ children }: { children: ReactNode }) => {
  const { clearPushNotifications } = usePushNotificationContext();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [commerceName, setCommerceName] = useState('');
  const [commerceLogo, setCommerceLogo] = useState('');
  const [commercePath, setCommercePath] = useState('');
  const [userId, setUserId] = useState('');
  const [userName, setUserName] = useState('');
  const [isReady, setIsReady] = useState(false);
  const [user, setUser] = useState({ user: { email: '', name: '', username: '', status: '' } });
  const [telemetrySessionId, setTelemetrySessionId] = useStoredState(TELEMETRY_STORAGE_KEY, EMPTY_VALUE);
  const [onboardingDate, setOnboardingDate] = useState<string | null>('');
  // PLI-2031: Acceso delegado — userType se persiste en localStorage, sesión delivery es efímera en memoria
  const [userType, setUserType] = useState<UserType | null>(null);
  const [isDeliveryPerson, setIsDeliveryPerson] = useState(false);
  const [deliveryPersonJwt, setDeliveryPersonJwt] = useState<string | null>(null);
  const [deliveryOrderId, setDeliveryOrderId] = useState<string | null>(null);
  const [deliveryDriverName, setDeliveryDriverName] = useState<string | null>(null);
  // Multisucursal: is_root y módulos del usuario se persisten en localStorage
  const [isRoot, setIsRoot] = useState(false);
  const [userModules, setUserModules] = useState<UserModule[]>([]);

  const posthog = usePostHog();

  // La identificación de telemetría (PostHog + Clarity + GA4) vive ahora en
  // useTelemetryIdentify (montado en App), que la re-aplica en cada sesión con un
  // payload enriquecido. Aquí solo se captura el session id de PostHog.

  const checkAuth = () => {
    setIsReady(false);
    const id_commerce = localStorage.getItem('id_commerce');
    const id_user = localStorage.getItem('id_user');
    const id_path = localStorage.getItem('id_path');
    const commerce_name = (localStorage.getItem('commerce_name') || '').toString();
    const commerce_logo = (localStorage.getItem('commerce_logo') || '').toString();
    const user_name = (localStorage.getItem('user_name') || '').toString();
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('userData');
    const onBoardingDate = localStorage.getItem('onboarding_finish_date');

    if (
      id_commerce &&
      id_user &&
      id_path &&
      commerce_name &&
      user_name &&
      typeof commerce_logo === 'string' &&
      token &&
      userData
    ) {
      setCommerceName(commerce_name);
      setCommerceLogo(commerce_logo);
      setCommercePath(id_path);
      setUserId(id_user);
      setUserName(user_name);
      setIsAuthenticated(true);
      setUser({ user: JSON.parse(userData) });
      setOnboardingDate(onBoardingDate);
      // PLI-2031: Restaurar userType de localStorage (solo admin/cashier/standard, NO delivery_person)
      const storedUserType = localStorage.getItem('user_type') as UserType | null;
      setUserType(storedUserType);
      // Multisucursal: Restaurar is_root tolerando el valor legacy del backend ('si'/'no').
      // Sesiones que hicieron login con el código viejo guardaron 'si'/'no' crudo; sin esto
      // un root ya logueado perdería el switch al recargar (hasta re-login). Auto-migramos
      // el valor a 'true'/'false' para self-heal sin necesidad de cerrar sesión.
      const storedIsRoot = localStorage.getItem('is_root');
      const normalizedStoredIsRoot = storedIsRoot === 'true' || storedIsRoot === 'si';
      if (storedIsRoot !== String(normalizedStoredIsRoot)) {
        localStorage.setItem('is_root', String(normalizedStoredIsRoot));
      }
      setIsRoot(normalizedStoredIsRoot);
      const storedUserModules = localStorage.getItem('user_modules');
      if (storedUserModules) {
        try {
          setUserModules(JSON.parse(storedUserModules));
        } catch {
          setUserModules([]);
        }
      }
    }
    setIsReady(true);

    if (!telemetrySessionId) {
      const newSessionId = posthog?.get_session_id();
      setTelemetrySessionId(newSessionId);
    }
  };

  const login = (params: loginProps) => {
    localStorage.setItem('id_commerce', params.id_commerce);
    localStorage.setItem('id_user', params.id_user);
    localStorage.setItem('id_path', params.id_path);
    localStorage.setItem('commerce_name', params.commerce_name);
    localStorage.setItem('commerce_logo', params.commerce_logo);
    localStorage.setItem('user_name', params.user_name);
    localStorage.setItem('token', params.token);
    localStorage.setItem(
      'userData',
      JSON.stringify({
        email: params.user.email,
        name: params.user.name,
        username: params.user.username,
        status: params.user.status,
      }),
    );
    if (params.onboarding_finish_date) localStorage.setItem('onboarding_finish_date', params.onboarding_finish_date);

    setCommerceName(params.commerce_name);
    setCommerceLogo(params.commerce_logo);
    setCommercePath(params.id_path);
    setUserId(params.id_user);
    setUserName(params.user_name);
    setIsAuthenticated(true);
    setUser({
      user: {
        email: params.user.email,
        name: params.user.name,
        username: params.user.username,
        status: params.user.status,
      },
    });
    setOnboardingDate(params.onboarding_finish_date);
    // PLI-2031: Persistir user_type en localStorage
    if (params.user_type) {
      localStorage.setItem('user_type', params.user_type);
      setUserType(params.user_type);
    } else {
      localStorage.removeItem('user_type');
      setUserType(null);
    }
    // Multisucursal: Persistir is_root como booleano normalizado. El backend envía
    // is_root como string 'si'/'no' (no boolean), así que normalizamos: 'si'/true → true.
    // Sin esto, 'no' sería truthy (mostraría "Crear sucursal" a no-root) y al recargar
    // checkAuth compararía 'si' === 'true' → false (root perdería el switch).
    const normalizedIsRoot = params.is_root === true || params.is_root === 'si';
    localStorage.setItem('is_root', String(normalizedIsRoot));
    setIsRoot(normalizedIsRoot);
    if (params.user_modules) {
      localStorage.setItem('user_modules', JSON.stringify(params.user_modules));
      setUserModules(params.user_modules);
    }

    if (!telemetrySessionId) {
      const newSessionId = posthog?.get_session_id();
      setTelemetrySessionId(newSessionId);
    }
  };

  const logout = () => {
    clearPushNotifications();
    resetStore();
    clearCache();
    clearDeliveryPersonSession(); // Limpiamos singletons del repartidor (Fix de Sentry)
    [
      'id_commerce',
      'id_user',
      'id_path',
      'commerce_name',
      'commerce_logo',
      'user_name',
      'token',
      'userData',
      'onboarding_finish_date',
      'user_type', // PLI-2031
      'is_root', // Multisucursal
      'user_modules', // Multisucursal
    ].forEach((item) => localStorage.removeItem(item));
    // Limpia el store de módulo del rollout DESPUÉS de borrar id_commerce: el reset notifica
    // a los hooks montados y re-inicializa el slot leyendo getActiveCommerceId(); si corriera
    // antes del removeItem, re-hidrataría con el cache del comercio que acabamos de cerrar.
    resetFeatureVersionStore();
    resetFeatureVersionHydration();
    resetOutboxDraining();
    // El preview per-usuario (per-tab) es del usuario que cierra sesión: no debe sobrevivir.
    resetPreviewState();
    setIsAuthenticated(false);
    setTelemetrySessionId('');
    // Telemetría: limpiar el hash de email cacheado para no contaminar la sesión
    // del próximo usuario antes de que resuelva su identify.
    resetUserHash();
    // PLI-2031: Limpiar sesión de repartidor
    setUserType(null);
    setIsDeliveryPerson(false);
    setDeliveryPersonJwt(null);
    setDeliveryOrderId(null);
    setDeliveryDriverName(null);
    // Multisucursal
    setIsRoot(false);
    setUserModules([]);
  };

  const refreshLogin = (userToken: string, commerceId: string, identity?: BranchIdentity) => {
    // Multisucursal (BUG-6): nunca cambiar a una sesión con token/comercio vacíos. Si un caller
    // pasa valores vacíos (p.ej. un pedido cuya sucursal no está en accountCommerceTokens), abortar
    // sin tocar localStorage para no borrar el token actual y provocar 401/logout involuntario.
    if (!userToken || !commerceId) {
      window.console.warn('refreshLogin abortado: token o id_comercio vacío');
      return;
    }
    setIsReady(false);
    resetStore();
    clearCache();
    localStorage.setItem('token', userToken);
    localStorage.setItem('id_commerce', commerceId);
    // Cambio de sucursal SIN reload (multisucursal): limpiar el store de módulo del rollout
    // DESPUÉS de fijar id_commerce, para que la re-inicialización del slot lea el cache del
    // comercio destino (getActiveCommerceId) y no herede la variante del comercio anterior.
    resetFeatureVersionStore();
    resetFeatureVersionHydration();
    resetOutboxDraining();
    // El preview per-usuario (per-tab) es del usuario/sucursal anterior: no debe sobrevivir.
    resetPreviewState();
    // Multisucursal: persistir la identidad de la sucursal destino para no dejar
    // commerce_name/logo/path con los valores de la sucursal anterior (trazabilidad).
    if (identity?.nombre) {
      localStorage.setItem('commerce_name', identity.nombre);
      setCommerceName(identity.nombre);
    }
    if (typeof identity?.logotipo === 'string') {
      localStorage.setItem('commerce_logo', identity.logotipo);
      setCommerceLogo(identity.logotipo);
    }
    if (identity?.id_path) {
      localStorage.setItem('id_path', identity.id_path);
      setCommercePath(identity.id_path);
    }
    // Re-identificar telemetría (PostHog + Clarity + GA4) con la sucursal activa para no
    // atribuir eventos a la anterior. Usa el facade unificado; useTelemetryIdentify también
    // re-aplicará la identidad enriquecida cuando el store de la nueva sucursal cargue.
    const idUser = localStorage.getItem('id_user') || '';
    const storedUserName = localStorage.getItem('user_name');
    identify(posthog, {
      idUser: idUser || commerceId,
      idCommerce: commerceId,
      idPath: identity?.id_path,
      commerceName: identity?.nombre,
      email: user.user.email,
      name: user.user.name,
      userName: storedUserName,
      userStatus: user.user.status,
    }).catch(() => {
      // noop — la identificación no debe romper el cambio de sucursal
    });
    requestAnimationFrame(() => setIsReady(true));
  };

  // PLI-2031: Login efímero para repartidores — JWT solo en memoria, no en localStorage
  const loginAsDeliveryPerson = (data: DeliveryPersonSessionData) => {
    setDeliveryPersonJwt(data.jwt);
    setDeliveryOrderId(data.orderId);
    setDeliveryDriverName(data.driverName);
    setUserType('delivery_person');
    setIsDeliveryPerson(true);
    setIsAuthenticated(true);
    setIsReady(true);
  };

  useEffect(() => {
    checkAuth();
  }, []);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        onboardingDate,
        isReady,
        login,
        logout,
        refreshLogin,
        checkAuth,
        commerceName,
        setCommerceName,
        commerceLogo,
        setCommerceLogo,
        commercePath,
        setCommercePath,
        userId,
        userName,
        user: {
          email: user.user.email,
          name: user.user.name,
          username: user.user.username,
          status: user.user.status,
        },
        // PLI-2031
        userType,
        isDeliveryPerson,
        deliveryPersonJwt,
        deliveryOrderId,
        deliveryDriverName,
        loginAsDeliveryPerson,
        // Multisucursal
        isRoot,
        hasUserModule: (moduleKey: string) => userModules.some((m) => m.id === moduleKey),
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export { AuthComponent, AuthContext, useAuth };
